// @flow

import * as React from 'react';
import classNames from '@ta/common.classnames';
import { featureIsEnabled } from '@ta/common.features';
import Icon from '@ta/core-ui.icon';
import { useLocalize } from '@ta/platform.i18n';
import styles from './BookmarkIcon.css';

/** The different bookmark styles */
export type BookmarkStyle = 'stacked' | 'flat' | 'outline';
/** The slightly insane way of enumerating the different sizes a bookmark can have */
export type BookmarkSize = '18' | '20' | '22' | '24' | '30';
/** Optional text style on active state, ex: on trips "Save" card */
export type BookmarkTextStyle = 'green';

type Props = {|
  iconShape: 'bookmark' | 'heart',
  /** Whether or not the bookmark appears active */
  active: boolean,
  /** The callback onClick */
  onClick: (SyntheticMouseEvent<*>) => mixed,
  /** UI style of the bookmark */
  bookmarkStyle: BookmarkStyle,
  /** Size of the bookmark */
  bookmarkSize: BookmarkSize,
  /** Display the  elements vertically as opposed to horizontally */
  vertical: boolean,
  /** Whether the children should be decorated on hover */
  decorated: boolean,
  /** Optional: Style override for line-height */
  lineHeight?: number,
  /** Optional: Text style on active state */
  bookmarkTextStyle?: BookmarkTextStyle,
  /** Optional: Animate the bookmark to grow and shrink */
  animated: boolean,
  /**
   * Optional: Additional elements to render as part of the bookmark.
   * A render prop which gives you the localized text of the active state
   */
  children?: (text: string) => ?React.Node,
|};

/** Private factories to create various bookmarks.
 *  These can't be in its own component because of hover states without complicated JS, hacky CSS, or smelly props,
 *  so they're just simple functions */

const sizeClasses = size => ({
  [styles.s_18]: size === '18',
  [styles.s_20]: size === '20',
  [styles.s_22]: size === '22',
  [styles.s_24]: size === '24',
  [styles.s_30]: size === '30',
});

// Convenience private function to build flat bookmarks
const flatFactory = (size, icon, isHeartEnabled: boolean) => (
  <span className={styles.icon_container}>
    <span
      className={classNames(styles.flatIcon, sizeClasses(size), {
        [styles.heart]: isHeartEnabled,
      })}
    >
      <Icon name={icon} />
    </span>
  </span>
);

// Convenience private function to build stacked bookmarks
const stackedFactory = (
  size: BookmarkSize,
  innerIcon,
  outerIcon,
  outline = false,
  isHeartEnabled: boolean,
  isAnimated: boolean,
) => (
  <span className={classNames(styles.icon_container, styles.stacked_bookmark, sizeClasses(size))}>
    <span className={styles.stacked_bookmark_inner}>
      <span
        className={classNames(styles.stacked_icon, styles.innerIcon, {
          [styles.outline]: outline,
          [styles.heart]: isHeartEnabled,
        })}
      >
        <Icon name={innerIcon} />
      </span>
      <span
        className={classNames(styles.stacked_icon, styles.outerIcon, {
          [styles.outline]: outline,
          [styles.heart]: isHeartEnabled,
          [styles.animated_heart]: isAnimated,
        })}
      >
        <Icon name={outerIcon} />
      </span>
    </span>
  </span>
);

// Convenience private function to create bookmarks.
const bookmarkFactory = (
  bookmarkStyle: BookmarkStyle,
  size: BookmarkSize,
  isHeartEnabled: boolean,
  isAnimated: boolean,
): React.Node => {
  const innerIcon = isHeartEnabled ? 'heart-fill' : 'bookmark-fill';
  const outerIcon = isHeartEnabled ? 'heart' : 'bookmark';
  if (bookmarkStyle === 'stacked') {
    return stackedFactory(size, innerIcon, outerIcon, false, isHeartEnabled, isAnimated);
  }
  if (bookmarkStyle === 'flat') {
    return flatFactory(size, innerIcon, isHeartEnabled);
  }
  if (bookmarkStyle === 'outline') {
    return stackedFactory(size, innerIcon, outerIcon, true, isHeartEnabled, isAnimated);
  }
  console.warn(`Bookmark style ${bookmarkStyle} not found.`);
  return null;
};

const BookmarkIcon = ({
  iconShape,
  bookmarkStyle,
  bookmarkSize,
  active,
  onClick,
  vertical,
  decorated,
  children,
  lineHeight,
  bookmarkTextStyle,
  animated,
}: Props) => {
  const isHeartEnabled = iconShape === 'heart';
  const bookmark = bookmarkFactory(
    bookmarkStyle,
    bookmarkSize,
    isHeartEnabled,
    isHeartEnabled && animated,
  );
  const { localize } = useLocalize();
  const saveText = active ? localize.string('social_Saved') : localize.string('trips_save_CTA');
  /* Saved : Save */

  const node = children?.(saveText);
  const overrideStyles = lineHeight && { lineHeight: `${lineHeight}px` };
  return (
    <div
      className={classNames(styles.container, {
        [styles.vertical]: vertical,
        [styles.no_decoration]: !decorated,
        [styles.active]: active,
        [styles.dark_icon]: featureIsEnabled('hr_dark_green_icons'),
        [styles.heart]: isHeartEnabled,
      })}
      onClick={onClick}
    >
      {bookmark}
      {node && (
        <div
          style={overrideStyles}
          className={classNames(styles.children, {
            [styles.text]: bookmarkTextStyle === 'green',
            [styles.heart]: isHeartEnabled,
          })}
        >
          {node}
        </div>
      )}
    </div>
  );
};

BookmarkIcon.defaultProps = {
  iconShape: featureIsEnabled('trips_saves_heart') ? 'heart' : 'bookmark',
  bookmarkSize: '30',
  active: false,
  onClick: () => {},
  vertical: false,
  children: () => null,
  decorated: true,
  lineHeight: undefined,
  animated: false,
};

export default BookmarkIcon;
